import React, { useEffect, useState } from 'react';
import { apiCall } from '../App';

const DEFAULT_CONFIG = {
  emailEnabled: true,
  emailRecipients: '',
  slackEnabled: false,
  slackWebhookUrl: '',
  alertOnFailure: true,
  alertOnSlow: true,
  slowThresholdMs: 3000,
  alertOnRecovery: true,
};

export default function AlertConfig() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await apiCall('/api/alerts/config');
        const c = res.config;
        setConfig({
          ...c,
          emailRecipients: Array.isArray(c.emailRecipients) ? c.emailRecipients.join(', ') : (c.emailRecipients || ''),
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function set(field, value) {
    setConfig(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...config,
        emailRecipients: config.emailRecipients
          .split(',').map(s => s.trim()).filter(Boolean),
        slowThresholdMs: parseInt(config.slowThresholdMs),
      };
      await apiCall('/api/alerts/config', { method: 'PUT', body: payload });
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Alert settings</h1>
          <p className="page-sub">Configure how and when you get notified about integration failures</p>
        </div>
      </div>

      <form onSubmit={handleSave}>
        {error && <div className="form-error" style={{ marginBottom: 16 }}>{error}</div>}
        {saved && <div className="form-success" style={{ marginBottom: 16 }}>Settings saved successfully</div>}

        {/* Email */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <h2 className="card-title">Email notifications</h2>
            <label className="toggle">
              <input type="checkbox" checked={config.emailEnabled} onChange={e => set('emailEnabled', e.target.checked)} />
              <span className="toggle-slider" />
            </label>
          </div>
          {config.emailEnabled && (
            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="form-label">Recipients (comma separated)</label>
              <input
                className="form-input"
                type="text"
                placeholder="you@company.com, oncall@company.com"
                value={config.emailRecipients}
                onChange={e => set('emailRecipients', e.target.value)}
              />
              <span className="form-hint">All people listed will receive email alerts</span>
            </div>
          )}
        </div>

        {/* Slack */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <h2 className="card-title">Slack notifications</h2>
            <label className="toggle">
              <input type="checkbox" checked={config.slackEnabled} onChange={e => set('slackEnabled', e.target.checked)} />
              <span className="toggle-slider" />
            </label>
          </div>
          {config.slackEnabled && (
            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="form-label">Slack Webhook URL</label>
              <input
                className="form-input mono"
                type="url"
                placeholder="https://hooks.slack.com/services/T.../B.../..."
                value={config.slackWebhookUrl}
                onChange={e => set('slackWebhookUrl', e.target.value)}
              />
              <span className="form-hint">
                Get this from Slack → Apps → Incoming Webhooks → Add new webhook
              </span>
            </div>
          )}
        </div>

        {/* Alert triggers */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h2 className="card-title" style={{ marginBottom: 16 }}>When to alert</h2>

          <div className="toggle-row">
            <div>
              <div className="toggle-label">Integration goes down</div>
              <div className="toggle-sub">Alert when a check fails or times out</div>
            </div>
            <label className="toggle">
              <input type="checkbox" checked={config.alertOnFailure} onChange={e => set('alertOnFailure', e.target.checked)} />
              <span className="toggle-slider" />
            </label>
          </div>

          <div className="toggle-row">
            <div>
              <div className="toggle-label">Integration is slow</div>
              <div className="toggle-sub">Alert when response time exceeds threshold</div>
            </div>
            <label className="toggle">
              <input type="checkbox" checked={config.alertOnSlow} onChange={e => set('alertOnSlow', e.target.checked)} />
              <span className="toggle-slider" />
            </label>
          </div>

          {config.alertOnSlow && (
            <div className="form-group" style={{ marginTop: 12, paddingLeft: 0 }}>
              <label className="form-label">Slow response threshold</label>
              <select className="form-select" value={config.slowThresholdMs} onChange={e => set('slowThresholdMs', e.target.value)}>
                <option value={1000}>1 second</option>
                <option value={2000}>2 seconds</option>
                <option value={3000}>3 seconds (recommended)</option>
                <option value={5000}>5 seconds</option>
                <option value={10000}>10 seconds</option>
              </select>
            </div>
          )}

          <div className="toggle-row">
            <div>
              <div className="toggle-label">Integration recovers</div>
              <div className="toggle-sub">Alert when a down integration comes back online</div>
            </div>
            <label className="toggle">
              <input type="checkbox" checked={config.alertOnRecovery} onChange={e => set('alertOnRecovery', e.target.checked)} />
              <span className="toggle-slider" />
            </label>
          </div>
        </div>

        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save alert settings'}
        </button>
      </form>
    </div>
  );
}
